"use client";

import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface FocusTrapOptions {
  /** Dipanggil saat Escape ditekan. Kalau diisi, hook ikut menangani Escape. */
  onEscape?: () => void;
  /** Kunci scroll <body> selama trap aktif (default: false demi kompatibilitas). */
  lockScroll?: boolean;
}

/**
 * Focus trap untuk modal/dialog buatan sendiri:
 * - memindahkan fokus ke dalam container saat mount
 * - mengunci Tab/Shift+Tab agar berputar di dalam container
 * - mengembalikan fokus ke elemen pemicu saat unmount
 * - opsional: menutup dengan Escape dan mengunci scroll body
 *
 * Dialog Radix (`components/ui/dialog.tsx`) sudah punya semua ini secara bawaan —
 * hook ini hanya untuk panel yang terlanjur ditulis tangan.
 */
export function useFocusTrap<T extends HTMLElement>(
  active: boolean = true,
  options: FocusTrapOptions = {}
): RefObject<T | null> {
  const containerRef = useRef<T>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  // Opsi disimpan di ref supaya object literal yang dibuat ulang tiap render
  // tidak memicu effect trap jalan berulang. Sinkronisasinya lewat effect —
  // menulis ref saat render melanggar aturan React (bisa salah di Strict Mode
  // dan concurrent rendering). Effect ini dideklarasikan LEBIH DULU sehingga
  // sudah terisi saat effect trap di bawah berjalan.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    let restoreOverflow: (() => void) | undefined;
    if (optionsRef.current.lockScroll) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      restoreOverflow = () => { document.body.style.overflow = previous; };
    }

    // Fokus ke elemen pertama yang bisa difokus (atau container-nya)
    const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE);
    (focusables[0] || container).focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && optionsRef.current.onEscape) {
        e.preventDefault();
        optionsRef.current.onEscape();
        return;
      }
      if (e.key !== "Tab") return;
      // Baca ulang dari ref — panel modal bisa di-remount (mis. step sukses)
      const trapEl = containerRef.current;
      if (!trapEl) return;
      const items = Array.from(trapEl.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (current === first || !trapEl.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !trapEl.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      restoreOverflow?.();
      previousFocusRef.current?.focus?.();
    };
  }, [active]);

  return containerRef;
}
